import React, { forwardRef } from "react";
import { useForm } from "react-hook-form";
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    Button,
    DialogContent,
    Field,
    Input,
    Checkbox,
    Dropdown,
    Option,
} from "@fluentui/react-components";
import { Alert } from '@fluentui/react-components/unstable';
import { Dismiss24Filled, CalendarLtrRegular } from "@fluentui/react-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { GLOBAL_STYLES } from "../../styles";
import DwsimProIcon from "../../assets/dwsim-pro-icon.png";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import 'react-phone-number-input/style.css';
import countryList from 'country-list';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getOrdersClient } from "../../services/dashboard.service";
import { OrderPostModel } from "../../swagger-clients/s365-dashboard-v2-api-clients.service";
import { processServerError } from "../../utils/helpers/error.helper";
import { useLoading } from "../../utils/loading-indicator.component";
import { S365Button } from "../s365-button/s365-button.component";

type PurchaseOrderFormDialogProps = {
    isOpened?: boolean;
    onClose: () => void;
};

type PurchaseOrderFormModel = {
    quoteNumber: string;
    purchaseOrderNumber: string;
    subscriptionStartDate: Date | null;
    purchaseOrderSender: string;
    purchaseOrderSenderRole: string;
    billingPersonNameAndSurname: string;
    billingPersonEmail: string;
    companyName: string;
    companyStreetName: string;
    companyStreetNumber: string;
    companyPostcode: string;
    companyCity: string;
    companyCountry: string;
    companyTelephone: string;
    companyVATNumber: string;
    endUserNameAndSurname: string;
    endUserEmailAddress: string;
    endUserTelephone: string;
    additionalInformation?: string;
    confirmInformation: boolean;
};

const formSchema = Yup.object().shape<Record<keyof PurchaseOrderFormModel, Yup.AnySchema>>({
    quoteNumber: Yup.string().required("Quote Number is required."),
    purchaseOrderNumber: Yup.string().required("Your Purchase Order Number is required."),
    subscriptionStartDate: Yup.date().required("Subscription start date is required.").nullable(),
    purchaseOrderSender: Yup.string().required("Purchase Order Sender is required."),
    purchaseOrderSenderRole: Yup.string().required("Purchase Order Sender's Role is required."),
    billingPersonNameAndSurname: Yup.string().required("Billing Person Name and Surname is required."),
    billingPersonEmail: Yup.string()
        .required("Billing Person email is required.")
        .email("Email is not in correct format."),
    companyName: Yup.string().required("Billing Company Name is required."),
    companyStreetName: Yup.string().required("Company street name is required."),
    companyStreetNumber: Yup.string().required("Company street number is required."),
    companyPostcode: Yup.string().required("Company postcode is required."),
    companyCity: Yup.string().required("Company city is required."),
    companyCountry: Yup.string().required("Company country is required."),
    companyTelephone: Yup.string()
        .required("Company telephone is required.")
        .test(
            'is-valid-phone',
            'Company telephone is not in correct format.',
            (value) => isValidPhoneNumber(value || '')
        ),
    companyVATNumber: Yup.string().required("Company VAT Number is required."),
    endUserNameAndSurname: Yup.string().required("End User Name and Surname is required."),
    endUserEmailAddress: Yup.string()
        .required("End User Email address is required.")
        .email("Email is not in correct format."),
    endUserTelephone: Yup.string()
        .required("End User telephone is required.")
        .test(
            'is-valid-phone',
            'End User telephone is not in correct format.',
            (value) => isValidPhoneNumber(value || '')
        ),
    additionalInformation: Yup.string().notRequired(),
    confirmInformation: Yup.boolean()
        .required("You must confirm that the information is correct.")
        .oneOf([true], "You must confirm that the information is correct."),
});

export const PurchaseOrderFormDialog: React.FC<PurchaseOrderFormDialogProps> = (props) => {
    const [open, setOpen] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [subscriptionStartDate, setSubscriptionStartDate] = React.useState<Date | null>(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [selectedCountry, setSelectedCountry] = React.useState<string>("");
    
    const countries = React.useMemo(() => {
        return countryList.getData().sort((a, b) => a.name.localeCompare(b.name));
    }, []);
    
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitted: formIsSubmitted },
        reset,
        setValue,
    } = useForm<PurchaseOrderFormModel>({
        resolver: yupResolver(formSchema) as any,
        mode: "onSubmit",
        reValidateMode: "onChange"
    });

    const CustomDateInputField = forwardRef((props: any, ref: any) => (
        <Field className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}>
            <Input
                value={props.value}
                onClick={props.onClick}
                contentAfter={<CalendarLtrRegular onClick={props.onClick} />}
                ref={ref} />
        </Field>
    ));

    React.useEffect(() => {
        setOpen(!!props.isOpened);
    }, [props.isOpened]);

    const onModalClose = () => {
        setOpen(false);
        setIsSubmitted(false);
        setSelectedCountry("");
        reset();
        if (props.onClose) {
            props.onClose();
        }
    };

    const onSubmit = async (data: PurchaseOrderFormModel) => {
        console.log("Form submitted:", data);
        // Handle form submission here
        setIsSubmitting(true);
        try {
            const client = getOrdersClient();
            const model = new OrderPostModel({
                quoteNumber: data.quoteNumber,
                purchaseOrderNumber: data.purchaseOrderNumber,
                subscriptionStartDate: data.subscriptionStartDate,
                purchaseOrderSender: data.purchaseOrderSender,
                purchaseOrderSenderRole: data.purchaseOrderSenderRole,
                billingPersonNameAndSurname: data.billingPersonNameAndSurname,
                billingPersonEmail: data.billingPersonEmail,
                companyName: data.companyName,
                companyStreetName: data.companyStreetName,
                companyStreetNumber: data.companyStreetNumber,
                companyPostcode: data.companyPostcode,
                companyCity: data.companyCity,
                companyCountry: data.companyCountry,
                companyTelephone: data.companyTelephone,
                companyVATNumber: data.companyVATNumber,
                endUserNameAndSurname: data.endUserNameAndSurname,
                endUserEmailAddress: data.endUserEmailAddress,
                endUserTelephone: data.endUserTelephone,
                additionalInformation: data.additionalInformation
            });
            await client.createOrder(model);
            setIsSubmitted(true);
        } catch (error) {
            processServerError(error, undefined, "An error occurred while submitting the purchase order form.");
        } finally {
            setIsSubmitting(false);
        }

    };

    return (
        <Dialog
            open={open}
            modalType="alert"
            inertTrapFocus
            onOpenChange={(event, data) => {
                setOpen(data.open);
                if (!data.open) {
                    onModalClose();
                }
            }}
        >
            <DialogSurface style={{ maxWidth: "900px" }}>
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                    <DialogBody style={{ maxWidth: "inherit" }}>
                        <DialogTitle
                            action={
                                <Button
                                    appearance="transparent"
                                    onClick={onModalClose}
                                    icon={<Dismiss24Filled />}
                                />
                            }
                        >
                            Purchase Order Form
                        </DialogTitle>
                        <DialogContent
                            className={GLOBAL_STYLES.DIALOG_CONTENT_WITH_SPACING}
                            style={{ paddingLeft: "40px", paddingRight: "40px" }}
                        >
                            {isSubmitted ? (
                                <div style={{ textAlign: "center", padding: "var(--spacingVerticalXXL)" }}>
                                    <p style={{ fontSize: "1.2em", lineHeight: "1.6" }}>
                                        Thank you for submitting the Purchase Order form.<br /> You will receive an invoice within the next 24 hours.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <p>
                                        <strong>Please ensure that all information required for correct invoicing is fully and accurately provided.</strong>
                                        <br />
                                        All purchases are subscription-based and will renew automatically according to the selected billing cycle until cancelled by the billing person.
                                    </p>
                                    <p >
                                        <strong>Note:</strong> The Billing Person serves as the License Manager and will assign the license to the selected End User.
                                        Both parties must have an account on <a href="https://dashboard.simulate365.com" target="_blank" rel="noopener noreferrer">dashboard.simulate365.com</a> for successful license management.
                                    </p>


                                    <div style={{ display: "flex", gap: "var(--spacingHorizontalXXL)", width: "100%" }}>
                                        {/* Left side - Product information */}
                                        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                            <img src={DwsimProIcon} alt="DWSIM Pro" style={{ width: "200px", height: "200px", objectFit: "contain", marginBottom: "var(--spacingVerticalXL)" }} />
                                            <h2 style={{ marginTop: 0, marginBottom: "var(--spacingVerticalM)" }}>DWSIM Pro Hourly - 2 hours</h2>
                                            <p style={{ fontSize: "14px", lineHeight: "1.5", marginBottom: "var(--spacingVerticalXL)" }}>
                                                1 named user, usage of 2 hours of DWSIM Pro within 1 month. Enabled sharing of usage hours. Support not included.
                                            </p>
                                            <div style={{ display: "flex", alignItems: "center", gap: "var(--spacingHorizontalS)" }}>
                                                <span style={{ fontSize: "32px", fontWeight: "bold" }}>€29.99</span>
                                                <div style={{ fontSize: "14px", display: "flex", flexDirection: "column", lineHeight: "1.2" }}>
                                                    <span>per</span>
                                                    <span>month</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right side - Form fields */}
                                        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Quote Number"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.quoteNumber ? "error" : "none"}
                                                    validationMessage={errors.quoteNumber?.message}
                                                >
                                                    <Input
                                                        id="quoteNumber"
                                                        {...register("quoteNumber")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Purchase Order Sender"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.purchaseOrderSender ? "error" : "none"}
                                                    validationMessage={errors.purchaseOrderSender?.message}
                                                >
                                                    <Input
                                                        id="purchaseOrderSender"
                                                        placeholder="Your Name and Surname"
                                                        {...register("purchaseOrderSender")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Purchase Order Sender's Role"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.purchaseOrderSenderRole ? "error" : "none"}
                                                    validationMessage={errors.purchaseOrderSenderRole?.message}
                                                >
                                                    <Input
                                                        id="purchaseOrderSenderRole"
                                                        placeholder="Your role at the company"
                                                        {...register("purchaseOrderSenderRole")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Your Purchase Order Number"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.purchaseOrderNumber ? "error" : "none"}
                                                    validationMessage={errors.purchaseOrderNumber?.message}
                                                >
                                                    <Input
                                                        id="purchaseOrderNumber"
                                                        {...register("purchaseOrderNumber")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Subscription start date"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.subscriptionStartDate ? "error" : "none"}
                                                    validationMessage={errors.subscriptionStartDate?.message}
                                                >
                                                    <DatePicker
                                                        selected={subscriptionStartDate}
                                                        dateFormat="dd.MM.yyyy."
                                                        onChange={(date) => {
                                                            setSubscriptionStartDate(date);
                                                            setValue("subscriptionStartDate", date, {
                                                                shouldDirty: true,
                                                                shouldTouch: true,
                                                                shouldValidate: formIsSubmitted,
                                                            });
                                                        }}
                                                        customInput={<CustomDateInputField />}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Billing Person Name and Surname"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.billingPersonNameAndSurname ? "error" : "none"}
                                                    validationMessage={errors.billingPersonNameAndSurname?.message}
                                                >
                                                    <Input
                                                        id="billingPersonNameAndSurname"
                                                        {...register("billingPersonNameAndSurname")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Billing Person email"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.billingPersonEmail ? "error" : "none"}
                                                    validationMessage={errors.billingPersonEmail?.message}
                                                >
                                                    <Input
                                                        id="billingPersonEmail"
                                                        type="email"
                                                        {...register("billingPersonEmail")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Billing Company Name"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.companyName ? "error" : "none"}
                                                    validationMessage={errors.companyName?.message}
                                                >
                                                    <Input
                                                        id="companyName"
                                                        {...register("companyName")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", gap: "var(--spacingHorizontalM)", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Street name"
                                                    required
                                                    style={{ flex: 2 }}
                                                    validationState={errors.companyStreetName ? "error" : "none"}
                                                    validationMessage={errors.companyStreetName?.message}
                                                >
                                                    <Input
                                                        id="companyStreetName"
                                                        {...register("companyStreetName")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                                <Field
                                                    label="Number"
                                                    required
                                                    style={{ flex: 1 }}
                                                    validationState={errors.companyStreetNumber ? "error" : "none"}
                                                    validationMessage={errors.companyStreetNumber?.message}
                                                >
                                                    <Input
                                                        id="companyStreetNumber"
                                                        {...register("companyStreetNumber")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", gap: "var(--spacingHorizontalM)", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Postcode"
                                                    required
                                                    style={{ flex: 1 }}
                                                    validationState={errors.companyPostcode ? "error" : "none"}
                                                    validationMessage={errors.companyPostcode?.message}
                                                >
                                                    <Input
                                                        id="companyPostcode"
                                                        {...register("companyPostcode")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                                <Field
                                                    label="City"
                                                    required
                                                    style={{ flex: 2 }}
                                                    validationState={errors.companyCity ? "error" : "none"}
                                                    validationMessage={errors.companyCity?.message}
                                                >
                                                    <Input
                                                        id="companyCity"
                                                        {...register("companyCity")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Country"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.companyCountry ? "error" : "none"}
                                                    validationMessage={errors.companyCountry?.message}
                                                >
                                                    <Dropdown
                                                        placeholder="Select a country"
                                                        value={selectedCountry}
                                                        selectedOptions={selectedCountry ? [selectedCountry] : []}
                                                        onOptionSelect={(e, data) => {
                                                            const country = data.optionValue || "";
                                                            setSelectedCountry(country);
                                                            setValue("companyCountry", country, {
                                                                shouldDirty: true,
                                                                shouldTouch: true,
                                                                shouldValidate: formIsSubmitted,
                                                            });
                                                        }}
                                                        disabled={isSubmitting}
                                                        positioning="below"
                                                    >
                                                        {countries.map((country) => (
                                                            <Option key={country.code} value={country.name}>
                                                                {country.name}
                                                            </Option>
                                                        ))}
                                                    </Dropdown>
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Company telephone"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.companyTelephone ? "error" : "none"}
                                                    validationMessage={errors.companyTelephone?.message}
                                                >
                                                    <PhoneInput
                                                        international
                                                        placeholder="Enter phone number"
                                                        {...register("companyTelephone")}
                                                        onChange={(value) => {
                                                            setValue("companyTelephone", value, {
                                                                shouldDirty: true,
                                                                shouldTouch: true,
                                                                shouldValidate: formIsSubmitted,
                                                            })
                                                        }}
                                                        defaultCountry="DE"
                                                        className="phone-input-field"
                                                        countrySelectProps={{
                                                            className: 'react-phone-number-input__country-select',
                                                            optionClassName: 'react-phone-number-input__country-select-option',
                                                            dividerClassName: 'react-phone-number-input__country-select-divider'
                                                        }}
                                                        style={{
                                                            '--PhoneInputCountryFlag-height': '0.9em',
                                                            '--PhoneInputCountryFlag-borderColor': 'transparent',
                                                            '--PhoneInputCountrySelectArrow-color': 'currentColor',
                                                        } as any}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Company VAT Number"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.companyVATNumber ? "error" : "none"}
                                                    validationMessage={errors.companyVATNumber?.message}
                                                >
                                                    <Input
                                                        id="companyVATNumber"
                                                        {...register("companyVATNumber")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="End User Name and Surname"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.endUserNameAndSurname ? "error" : "none"}
                                                    validationMessage={errors.endUserNameAndSurname?.message}
                                                >
                                                    <Input
                                                        id="endUserNameAndSurname"
                                                        {...register("endUserNameAndSurname")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="End User Email address"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.endUserEmailAddress ? "error" : "none"}
                                                    validationMessage={errors.endUserEmailAddress?.message}
                                                >
                                                    <Input
                                                        id="endUserEmailAddress"
                                                        type="email"
                                                        {...register("endUserEmailAddress")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalL)" }}>
                                                <Field
                                                    label="End User telephone"
                                                    required
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.endUserTelephone ? "error" : "none"}
                                                    validationMessage={errors.endUserTelephone?.message}
                                                >
                                                    <PhoneInput
                                                        international
                                                        placeholder="Enter phone number"
                                                        {...register("endUserTelephone")}
                                                        onChange={(value) => {
                                                            setValue("endUserTelephone", value, {
                                                                shouldDirty: true,
                                                                shouldTouch: true,
                                                                shouldValidate: formIsSubmitted,
                                                            })
                                                        }}
                                                        defaultCountry="DE"
                                                        className="phone-input-field"
                                                        countrySelectProps={{
                                                            className: 'react-phone-number-input__country-select',
                                                            optionClassName: 'react-phone-number-input__country-select-option',
                                                            dividerClassName: 'react-phone-number-input__country-select-divider'
                                                        }}
                                                        style={{
                                                            '--PhoneInputCountryFlag-height': '0.9em',
                                                            '--PhoneInputCountryFlag-borderColor': 'transparent',
                                                            '--PhoneInputCountrySelectArrow-color': 'currentColor',
                                                        } as any}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalS)" }}>
                                                <Field
                                                    label="Additional information that should appear on your invoice"
                                                    className={GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}
                                                    validationState={errors.additionalInformation ? "error" : "none"}
                                                    validationMessage={errors.additionalInformation?.message}
                                                >
                                                    <Input
                                                        id="additionalInformation"
                                                        {...register("additionalInformation")}
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ marginBottom: "var(--spacingVerticalM)" }}>
                                                <Field
                                                    validationState={errors.confirmInformation ? "error" : "none"}
                                                    validationMessage={errors.confirmInformation?.message}
                                                >
                                                    <Checkbox
                                                        {...register("confirmInformation")}
                                                        label="I confirm that the information provided is correct and that by submitting this form, I commit to the purchase."
                                                        disabled={isSubmitting}
                                                    />
                                                </Field>
                                            </div>

                                            <div style={{ marginBottom: "var(--spacingVerticalL)" }}>
                                                <Checkbox
                                                    checked={true}
                                                    disabled={true}
                                                    label={
                                                        <span style={{ color: "var(--colorNeutralForeground1)", opacity: 1 }}>
                                                            During the sign up process you agreed to the CGC Capital-Gain Consultants <a
                                                                href="/privacy-policy/CGC-PrivacyPolicy.html"
                                                                target="_blank"
                                                            >
                                                                Privacy Policy
                                                            </a> and <a
                                                                href="/eula/CGC-Cloud-EULA.html"
                                                                target="_blank"
                                                            >
                                                                Cloud End-User License Agreement (EULA)
                                                            </a>.
                                                        </span>
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "center", marginTop: "var(--spacingVerticalXL)" }}>
                                        <S365Button appearance="primary" type="submit" size="large" isLoading={isSubmitting}>
                                            Send Purchase Order
                                        </S365Button>
                                    </div>
                                </>
                            )}
                        </DialogContent>
                    </DialogBody>
                </form>
            </DialogSurface>
        </Dialog>
    );
};
